import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CONTACT_INFO } from '../data/siteData';
import { Phone, Mail, MapPin, Clock, CreditCard, Send, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../services/api';

interface ContactFormState {
  azienda: string;
  nome: string;
  email: string;
  telefono: string;
  oggetto: string;
  messaggio: string;
}

const EMPTY_FORM: ContactFormState = {
  azienda: '', nome: '', email: '', telefono: '', oggetto: '', messaggio: '',
};

export default function ContactPage() {
  const [form, setForm] = useState<ContactFormState>(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field: keyof ContactFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.oggetto.trim() || !form.messaggio.trim()) {
      toast.error('Compila tutti i campi obbligatori (*)');
      return;
    }
    setSending(true);
    try {
      await api.post('/api/contact', {
        azienda: form.azienda.trim() || null,
        nome: form.nome.trim(),
        email: form.email.trim(),
        telefono: form.telefono.trim() || null,
        oggetto: form.oggetto,
        messaggio: form.messaggio.trim(),
      });
      toast.success('Messaggio inviato con successo! Ti risponderemo al più presto.');
      setSent(true);
      setForm(EMPTY_FORM);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Invio non riuscito. Riprova più tardi o contattaci telefonicamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-8">Info & Contatti</h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="rounded-xl border bg-white p-6 sm:p-8">
              <h2 className="text-lg font-semibold mb-6">Richiesta Informazioni</h2>
              {sent ? (
                <div className="flex flex-col items-center text-center gap-3 py-10" data-testid="contact-form-success">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                  <p className="font-medium">Messaggio inviato con successo!</p>
                  <p className="text-sm text-muted-foreground">Ti risponderemo al più presto.</p>
                  <button
                    type="button"
                    data-testid="contact-form-send-another"
                    onClick={() => setSent(false)}
                    className="text-sm text-[hsl(var(--dtk-focus))] hover:underline mt-2"
                  >
                    Invia un altro messaggio
                  </button>
                </div>
              ) : (
              <form className="space-y-4" onSubmit={handleSubmit} noValidate data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium mb-1.5 block">Azienda</label>
                    <input value={form.azienda} onChange={update('azienda')} data-testid="contact-form-azienda" className="w-full h-10 px-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]" placeholder="Ragione sociale" />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1.5 block">Nome & Cognome *</label>
                    <input value={form.nome} onChange={update('nome')} required data-testid="contact-form-nome" className="w-full h-10 px-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]" placeholder="Mario Rossi" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium mb-1.5 block">Email *</label>
                    <input type="email" value={form.email} onChange={update('email')} required data-testid="contact-form-email" className="w-full h-10 px-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]" placeholder="email@azienda.it" />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1.5 block">Telefono</label>
                    <input value={form.telefono} onChange={update('telefono')} data-testid="contact-form-telefono" className="w-full h-10 px-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]" placeholder="+39 ..." />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1.5 block">Oggetto</label>
                  <select
                    className="w-full h-10 px-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                    data-testid="contact-form-oggetto"
                    value={form.oggetto}
                    onChange={update('oggetto')}
                    required
                  >
                    <option value="" disabled>Seleziona un oggetto...</option>
                    <option value="Richiesta Contatto Commerciale">Richiesta Contatto Commerciale</option>
                    <option value="Richiesta Prodotto non a Catalogo">Richiesta Prodotto non a Catalogo</option>
                    <option value="Richiesta Informazioni">Richiesta Informazioni</option>
                    <option value="Altro">Altro</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1.5 block">Messaggio *</label>
                  <textarea value={form.messaggio} onChange={update('messaggio')} required data-testid="contact-form-messaggio" className="w-full h-32 px-3 py-2 rounded-lg border text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]" placeholder="Scrivi il tuo messaggio..." />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit-button"
                  className="h-10 px-6 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center gap-2 hover:bg-[hsl(214,58%,46%)] transition-colors duration-150 active:scale-[0.99] disabled:opacity-60"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? 'Invio in corso...' : 'Invia Messaggio'}
                </button>
              </form>
              )}
            </div>
          </div>

          {/* Info Cards */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-xl border bg-white p-5">
              <h3 className="text-sm font-semibold mb-4">Recapiti</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" /><span>{CONTACT_INFO.address}</span></div>
                <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" /><span>{CONTACT_INFO.phone}</span></div>
                <div className="flex items-center gap-3"><Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" /><span>{CONTACT_INFO.hours}</span></div>
              </div>
            </div>

            <div className="rounded-xl border bg-white p-5">
              <h3 className="text-sm font-semibold mb-4">Email per Reparto</h3>
              <div className="space-y-2">
                {CONTACT_INFO.emails.map(e => (
                  <div key={e.dept} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{e.dept}</span>
                    <a href={`mailto:${e.email}`} className="text-[hsl(var(--dtk-focus))] hover:underline font-mono text-xs">{e.email}</a>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border bg-white p-5">
              <h3 className="text-sm font-semibold mb-3">Coordinate Bancarie</h3>
              <div className="text-sm space-y-1">
                <div className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-muted-foreground" /><span className="font-mono text-xs">{CONTACT_INFO.iban}</span></div>
                <div className="text-xs text-muted-foreground">{CONTACT_INFO.bank}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
